<template>
  <q-dialog v-model="isVisible">
    <q-card style="width: 600px; max-width: 80vw">
      <q-form
        autocorrect="off"
        autocapitalize="off"
        autocomplete="off"
        spellcheck="false"
        @submit="onSubmit"
      >
        <q-card-section class="row items-center justify-between">
          <span class="text-h5">
            {{
              isEditing
                ? $t('components.doctorModal.updateTitle')
                : $t('components.doctorModal.addTitle')
            }}
          </span>

          <q-btn
            :disable="isLoading"
            icon="close"
            flat
            round
            dense
            v-close-popup
          />
        </q-card-section>

        <q-card-section v-if="error" class="q-pt-none">
          <q-banner class="text-white bg-red" rounded>
            {{ error.message }}
          </q-banner>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6">
              <q-input
                v-model.trim="doctor.user.fullName"
                :label="$t('components.doctorModal.labels.fullName')"
                :error="v$.doctor.user.fullName.$error"
                :error-message="$t('components.doctorModal.errors.fullName')"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-6">
              <q-input
                v-model.trim="doctor.user.email"
                :label="$t('components.doctorModal.labels.email')"
                :error="v$.doctor.user.email.$error"
                :error-message="$t('components.doctorModal.errors.email')"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-6">
              <q-input
                v-model.trim="doctor.user.password"
                :label="$t('components.doctorModal.labels.password')"
                :error="v$.doctor.user.password.$error"
                :error-message="$t('components.doctorModal.errors.password')"
                type="password"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-6">
              <q-select
                v-model="doctor.user.country"
                :options="dealerCountries"
                :label="$t('components.doctorModal.labels.country')"
                :error="v$.doctor.user.country.$error"
                :error-message="$t('components.doctorModal.errors.country')"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-6">
              <div class="row q-col-gutter-md">
                <div class="col-8">
                  <q-input
                    v-model.trim="doctor.user.city"
                    :label="$t('components.doctorModal.labels.city')"
                    :error="v$.doctor.user.city.$error"
                    :error-message="$t('components.doctorModal.errors.city')"
                    hide-bottom-space
                  />
                </div>

                <div class="col-4">
                  <q-input
                    v-model.trim="doctor.user.postalCode"
                    :label="$t('components.doctorModal.labels.postalCode')"
                    :error="v$.doctor.user.postalCode.$error"
                    :error-message="
                      $t('components.doctorModal.errors.postalCode')
                    "
                    hide-bottom-space
                  />
                </div>
              </div>
            </div>

            <div class="col-12 col-md-6">
              <q-input
                v-model.trim="doctor.user.address"
                :label="$t('components.doctorModal.labels.address')"
                :error="v$.doctor.user.address.$error"
                :error-message="$t('components.doctorModal.errors.address')"
                hide-bottom-space
              />
            </div>

            <div class="col-12 col-md-6">
              <q-input
                v-model.trim="doctor.user.contactPhone"
                :label="$t('components.doctorModal.labels.contactPhone')"
                :error="v$.doctor.user.contactPhone.$error"
                :error-message="
                  $t('components.doctorModal.errors.contactPhone')
                "
                hide-bottom-space
              />
            </div>

            <div v-if="isEditing" class="col-12 col-md-6">
              <q-checkbox
                v-model="doctor.user.isActive"
                :label="$t('components.doctorModal.labels.status')"
              />
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn :disable="isLoading" color="primary" flat @click="close">
            {{ $t('components.doctorModal.cancelButtonTitle') }}
          </q-btn>

          <q-btn
            :loading="isLoading"
            :label="
              isEditing
                ? $t('components.doctorModal.updateButtonTitle')
                : $t('components.doctorModal.addButtonTitle')
            "
            type="submit"
            color="primary"
            flat
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import useValidate from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';

import { addDoctor, updateDoctor } from '@/api/doctors';

export default {
  data() {
    return {
      v$: useValidate(),
      isVisible: false,
      isLoading: false,
      error: null,
      doctor: {
        user: {
          email: null,
          password: null,
          fullName: null,
          country: null,
          city: null,
          postalCode: null,
          address: null,
          contactPhone: null,
        },
      },
    };
  },
  validations() {
    const validationRules = {
      doctor: {
        user: {
          email: {
            required,
            email,
          },
          password: {},
          fullName: {
            required,
          },
          country: {},
          city: {},
          postalCode: {},
          address: {},
          contactPhone: {},
        },
      },
    };

    if (!this.isEditing) {
      validationRules.doctor.user.password.required = required;
    }

    return validationRules;
  },
  computed: {
    ...mapGetters({
      dealerCountries: 'account/countries',
    }),
    isEditing() {
      return typeof this.doctor?.userId === 'number';
    },
  },
  methods: {
    open(doctor) {
      this.error = null;

      if (doctor) {
        this.doctor = _.cloneDeep({
          ...doctor,
          user: {
            ...doctor.user,
            password: null,
          },
        });
      } else {
        this.clearDoctor();
      }

      this.v$.$reset();

      this.isLoading = false;
      this.isVisible = true;
    },
    close() {
      this.isVisible = false;
    },
    clearDoctor() {
      this.doctor = {
        user: {
          email: null,
          password: null,
          fullName: null,
          country: null,
          city: null,
          postalCode: null,
          address: null,
          contactPhone: null,
        },
      };
    },
    async onSubmit() {
      this.error = null;

      this.v$.$touch();

      if (!this.v$.$invalid) {
        try {
          this.isLoading = true;

          if (this.isEditing) {
            const doctor = await updateDoctor(this.doctor);

            this.$emit('doctor-updated', doctor);
          } else {
            const doctor = await addDoctor(this.doctor);

            this.$emit('doctor-added', doctor);
          }

          this.close();
        } catch (error) {
          this.error = error;
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
};
</script>
